import { useEffect } from "react";

/** How long to keep correcting for late-arriving content, in milliseconds. */
const SETTLE_WINDOW_MS = 2000;

/**
 * Scrolls to the URL's hash target and keeps it there while the page settles.
 *
 * Two things fight a deep link here: the browser resolves `#roster` before
 * React has rendered anything, and the roster then grows by twenty-odd rows
 * when its data arrives, pushing every later section down. So rather than
 * scrolling once, this re-aligns whenever the document height changes, for a
 * short window after mount.
 */
export function useHashScroll(): void {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;

    let cancelled = false;
    const align = () => {
      if (cancelled) return;
      // Re-query each time: the section may not have existed on the first pass.
      // "instant" is required — "auto" inherits the page's smooth scrolling, and
      // each re-align would then restart an animation the next one interrupts.
      document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
    };

    align();

    const observer = new ResizeObserver(align);
    observer.observe(document.body);

    // Stop correcting once the page has had time to finish loading, so the
    // reader is never yanked back after they start scrolling themselves.
    const stop = window.setTimeout(() => {
      cancelled = true;
      observer.disconnect();
    }, SETTLE_WINDOW_MS);

    const release = () => {
      cancelled = true;
      observer.disconnect();
    };
    // Any deliberate input hands control straight back to the reader.
    window.addEventListener("wheel", release, { passive: true, once: true });
    window.addEventListener("touchstart", release, { passive: true, once: true });
    window.addEventListener("keydown", release, { once: true });

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(stop);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchstart", release);
      window.removeEventListener("keydown", release);
    };
  }, []);
}
